import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import { FeaturedInitialFormData } from "@saleor/categories/components/CategoryFeaturedCheckbox";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CategoryListPage } from "../../components/CategoryListPage/CategoryListPage";
import {
  useCategoryBulkDeleteMutation,
  useCategoryUpdateMutation
} from "../../mutations";
import { useRootCategoriesQuery } from "../../queries";
import { CategoryBulkDelete } from "../../types/CategoryBulkDelete";
import {
  categoryAddUrl,
  categoryListUrl,
  CategoryListUrlDialog,
  CategoryListUrlFilters,
  CategoryListUrlQueryParams,
  categoryUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterVariables,
  saveFilterTab
} from "./filter";
import { getSortQueryVariables } from "./sort";

interface CategoryListProps {
  params: CategoryListUrlQueryParams;
  levels;
}

export const CategoryList: React.FC<CategoryListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const { isSelected, listElements, toggle, toggleAll, reset } = useBulkActions(
    params.ids
  );
  const { updateListSettings, settings } = useListSettings(
    ListViews.CATEGORY_LIST
  );
  const intl = useIntl();

  const paginationState = createPaginationState(settings.rowNumber, params);
  const { reset: resetSelected } = useBulkActions(params.ids);
  const [lvl, setLvl] = React.useState(0);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      levels: lvl || params.levels,
      sort: getSortQueryVariables(params)
    }),
    [params, settings.rowNumber]
  );
  const { data, loading, refetch } = useRootCategoriesQuery({
    displayLoader: true,
    variables: queryVariables
  });

  const tabs = [
    ...[
      {
        data: {
          levels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
        },
        name: "ALL"
      },
      {
        data: {
          levels: [0]
        },
        name: "Main Categories"
      },
      {
        data: {
          levels: [1, 2, 3, 4, 5, 6, 7, 8, 9]
        },
        name: "Sub Categories"
      }
    ]
  ];

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length
        : 0
      : parseInt(params.activeTab, 0);

  const selectedTabs = [
    ...[
      {
        data: {
          level: currentTab
        },
        name: "ALL"
      },
      {
        data: {
          level: 0
        },
        name: "Main Categories"
      },
      {
        data: {
          level: 1
        },
        name: "Sub Categories"
      }
    ]
  ];

  const changeFilterField = (filter: CategoryListUrlFilters) => {
    resetSelected();
    navigate(
      categoryListUrl({
        ...getActiveFilters(params),
        ...filter,
        activeTab: params.activeTab
      })
    );
    setLvl(null);
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    CategoryListUrlDialog,
    CategoryListUrlQueryParams
  >(navigate, categoryListUrl, params);

  const handleTabChange = (tab: string | number) => {
    resetSelected();
    navigate(
      categoryListUrl({
        ...params,
        activeTab: tab.toString(),
        ...tabs[tab].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(categoryListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.categories.pageInfo),
    paginationState,
    params
  );

  const handleCategoryBulkDelete = (data: CategoryBulkDelete) => {
    if (data.categoryBulkDelete.errors.length === 0) {
      navigate(categoryListUrl(), true);
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.successCategoryDeletion)
      });
      refetch();
      reset();
    }
  };

  const [hasProducts, setHasProducts] = useState(false);
  useEffect(() => {
    const newCategories =
      data && data.categories
        ? data.categories.edges.filter(edge =>
            listElements.includes(edge.node.id)
          )
        : [];
    const catWithProducts = newCategories.filter(
      category => category.node.products.totalCount > 0
    );
    if (catWithProducts.length) {
      setHasProducts(true);
    } else {
      setHasProducts(false);
    }
  }, [listElements]);

  const [
    categoryBulkDelete,
    categoryBulkDeleteOpts
  ] = useCategoryBulkDeleteMutation({
    onCompleted: handleCategoryBulkDelete
  });

  const handleSort = createSortHandler(navigate, categoryListUrl, params);

  const [updateIsFeatured] = useCategoryUpdateMutation({
    onCompleted: event => {
      const { errors } = event.categoryUpdate;
      if (errors.length > 0) {
        notify({
          status: "error",
          text: errors[0].message
        });
      }
      refetch();
    }
  });

  const handleSubmit = async (data: FeaturedInitialFormData) => {
    updateIsFeatured({
      variables: { id: data.id, input: { isFeatured: !data.isFeatured } }
    });
    return null;
  };
  const mainCategory = params.activeTab === "1" || false;

  // const filteredCategories =
  //   data && data.categories?.edges && params.query
  //     ? data.categories?.edges
  //     : data.categories?.edges.filter(edge => edge.node.level === 0);
  // console.log(data.categories);
  // console.log(tabs.map(tab => tab))
  return (
    <>
      <CategoryListPage
        brands={null}
        activeTab={params.activeTab}
        // categories={maybe(() => filteredCategories.map(edge => edge.node), [])}
        categories={maybe(() => data.categories.edges.map(edge => edge.node))}
        currentTab={currentTab}
        initialSearch={params.query || ""}
        onSearchChange={query => changeFilterField({ query })}
        onAll={() => navigate(categoryListUrl())}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={selectedTabs.map(tab => tab.name)}
        settings={settings}
        sort={getSortParams(params)}
        onAdd={() => navigate(categoryAddUrl())}
        onRowClick={id => () => navigate(categoryUrl(id))}
        onSort={handleSort}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        mainCategory={mainCategory}
        onSubmit={handleSubmit}
        toolbar={
          <IconButton
            id="categoryDeleteBtn"
            color="primary"
            disabled={hasProducts}
            onClick={() =>
              openModal("delete", {
                ids: listElements
              })
            }
          >
            <DeleteIcon />
          </IconButton>
        }
        customAction={undefined}
      />
      <ActionDialog
        confirmButtonState={categoryBulkDeleteOpts.status}
        onClose={() =>
          navigate(
            categoryListUrl({
              ...params,
              action: undefined,
              ids: undefined
            })
          )
        }
        onConfirm={() =>
          categoryBulkDelete({
            variables: {
              ids: params.ids
            }
          })
        }
        open={params.action === "delete"}
        title={intl.formatMessage({
          defaultMessage: "Delete categories",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this category?} other{Are you sure you want to delete {displayQuantity} categories?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage defaultMessage="Remember this will also delete all products assigned to this category." />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default CategoryList;
